<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div ref="editorContainer" class="codemirror-wrapper"></div>
</template>

<script>
import {
  getLanguageExtension,
  getBaseExtensions,
  EditorState,
  EditorView,
} from "@/composables/useCodeMirror.js";
import { useTheme } from "@/composables/useTheme.js";

const LANGUAGE_TO_MIME = {
  json: "application/json",
  markdown: "text/markdown",
  html: "text/html",
  xml: "application/xml",
  yaml: "text/yaml",
  shell: "application/x-sh",
  text: "text/plain",
};

export default {
  name: "CodeMirrorBase",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    language: {
      type: String,
      default: "markdown",
      validator: (value) =>
        ["markdown", "json", "html", "xml", "yaml", "shell", "text"].includes(
          value,
        ),
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      editorView: null,
    };
  },
  watch: {
    modelValue(newValue) {
      if (
        this.editorView &&
        newValue !== this.editorView.state.doc.toString()
      ) {
        this.editorView.dispatch({
          changes: {
            from: 0,
            to: this.editorView.state.doc.length,
            insert: newValue,
          },
        });
      }
    },
  },
  mounted() {
    this.createEditor();
  },
  beforeUnmount() {
    this.destroyEditor();
  },
  methods: {
    createEditor() {
      if (!this.$refs.editorContainer) {
        return;
      }

      const { isDarkMode } = useTheme();
      const mimeType = LANGUAGE_TO_MIME[this.language] || "text/plain";
      const langExtension = getLanguageExtension(mimeType);

      const baseExtensions = getBaseExtensions({
        editable: true,
        showLineNumbers: true,
        enableHistory: true,
        dark: isDarkMode(),
        onUpdate: (content) => {
          this.$emit("update:modelValue", content);
        },
      });

      const extensions = [...baseExtensions];
      if (
        langExtension &&
        (Array.isArray(langExtension) ? langExtension.length > 0 : true)
      ) {
        extensions.push(langExtension);
      }

      const state = EditorState.create({
        doc: this.modelValue,
        extensions,
      });

      this.editorView = new EditorView({
        state,
        parent: this.$refs.editorContainer,
      });
    },
    destroyEditor() {
      if (this.editorView) {
        this.editorView.destroy();
        this.editorView = null;
      }
    },
  },
};
</script>

<style scoped>
.codemirror-wrapper {
  height: 100%;
  width: 100%;
  overflow: auto;
}

.codemirror-wrapper :deep(.cm-editor) {
  height: 100%;
  font-size: 14px;
}
</style>
